import React, { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { getItem } from "../../../../utils/localStorage";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import styles from "./invoice.module.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Layout from "../../component/layout";
import {
  getInvoice,
  get_lease,
  getCompanyInformationURL,
  fetchFacilityPaymentDetails,
  resendInvoiceNotification,
  sendInvoiceToManager,
  fetchPropertyManagersURL,
  getBankDetailsForLevyURL,
  getBillerAddressForLevyURL,
  getFacilityPaymentDetailsURL,
} from "../../../../utils/urls";
import { useFacilityStore } from "../../../../app/z-store/store";
import InvoiceActionsDropdown from "./InvoiceActionsDropdown";
import EtimsQRCode from "../../../common/EtimsQRCode";

const InvoicePage = () => {
  // State management
  const routerLocation = useLocation();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReceiptsDialog, setShowReceiptsDialog] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [facilityLogo, setFacilityLogo] = useState("");
  const [bankDetails, setBankDetails] = useState(null);
  const [billerAddress, setBillerAddress] = useState(null);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [leaseDetails, setLeaseDetails] = useState(null);

  // Individual loading states
  const [billerAddressLoading, setBillerAddressLoading] = useState(false);
  const [bankDetailsLoading, setBankDetailsLoading] = useState(false);
  const [paymentSettingsLoading, setPaymentSettingsLoading] = useState(false);
  const [leaseLoading, setLeaseLoading] = useState(false);

  // Hooks
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const facilityId = useFacilityStore((state) => state.facilityId);

  // Check if all payment data has loaded
  const allPaymentDataLoaded =
    !leaseLoading &&
    !billerAddressLoading &&
    !bankDetailsLoading &&
    !paymentSettingsLoading;

  // Fetch data on component mount
  useEffect(() => {
    fetchInvoiceData();
    fetchCompanyDetails();
    getFacilityLogo();
  }, [invoiceId, facilityId]);

  // Fetch payment details when invoice is loaded
  useEffect(() => {
    if (invoice && invoice.whatFor) {
      // For leases, check if there's a lease ID in whatFor
      if (invoice.whatFor.invoiceType === "Lease" && invoice.whatFor.description) {
        fetchPaymentDetailsForLease(invoice.whatFor.description);
      }
    }
  }, [invoice]);

  // Fetch facility logo
  const getFacilityLogo = async () => {
    try {
      const facilityFromState = routerLocation.state?.facility;

      if (facilityFromState?.logo) {
        const fullLogoUrl = facilityFromState.logo;
        localStorage.setItem("selectedFacilityLogo", fullLogoUrl);
        setFacilityLogo(fullLogoUrl);
      } else {
        const storedLogo = localStorage.getItem("selectedFacilityLogo");
        if (storedLogo) {
          setFacilityLogo(storedLogo);
        } else {
          setFacilityLogo("");
        }
      }
    } catch (error) {
      console.error("Error fetching facility logo:", error);
      setFacilityLogo("");
    }
  };

  // Fetch company details
  const fetchCompanyDetails = async () => {
    try {
      const response = await makeRequest2(

        `${getCompanyInformationURL}/${facilityId}`,
        "GET",
      );
      if (response.success) {
        setCompanyDetails(response.data);
      } else {
        toastify(
          response.message || "Failed to fetch company details",
          "error",
        );
      }
    } catch (error) {
      toastify(error.message, "error");
    }
  };

  // Fetch invoice data
  const fetchInvoiceData = async () => {
    setLoading(true);
    const facilityId = await getItem("selectedFacilityId");
    try {
      const response = await makeRequest2(
        `${getInvoice}/${facilityId}/${invoiceId}`,
        "GET",
      );

      if (response.success) {
        const invoiceData = response.data?.data || response.data;
        setInvoice(invoiceData);
      } else {
        toastify(response.message || "Failed to fetch invoice", "error");
      }
    } catch (error) {
      toastify(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to safely extract data
  const extractResponseData = (response) => {
    if (!response) return null;

    if (response.success && response.data) {
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      return response.data;
    }

    return response;
  };

  // Fetch lease by ID
  const fetchLeaseById = async (leaseId) => {
    try {
      const response = await makeRequest2(
        `${get_lease}/${facilityId}/${leaseId}`,
        "GET",
      );

      if (response.success) {
        const leaseData = extractResponseData(response);
        return leaseData;
      }
      return null;
    } catch (error) {
      console.error("Error fetching lease:", error);
      return null;
    }
  };

  const fetchPaymentDetailsForLease = async (leaseId) => {
    try {
      setLeaseLoading(true);
      setBillerAddressLoading(true);
      setBankDetailsLoading(true);
      setPaymentSettingsLoading(true);

      // Reset previous data
      setBankDetails(null);
      setBillerAddress(null);
      setPaymentSettings(null);
      setPaymentMethod(null);

      const leaseResponse = await makeRequest2(
        `${get_lease}/${facilityId}/${leaseId}`,
        "GET",
      );

      if (!leaseResponse.success) {
        toastify("Failed to fetch lease details", "error");
        return;
      }

      const leaseData = extractResponseData(leaseResponse);

      if (!leaseData) {
        toastify("No lease data found", "error");
        return;
      }

      setLeaseDetails(leaseData);

      // Process payment configuration from lease
      await processLeasePaymentData(leaseData);
    } catch (error) {
      console.error("Error fetching lease details:", error);
      toastify("Error fetching lease details", "error");
      setBillerAddressLoading(false);
      setBankDetailsLoading(false);
      setPaymentSettingsLoading(false);
    } finally {
      setLeaseLoading(false);
    }
  };

  const processLeasePaymentData = async (lease) => {
    try {
      console.log("Processing lease payment data:", lease);

      const promises = [];
      let hasBillerAddress = false;
      let hasPaymentMethod = false;

      // Check for biller address
      if (lease.property?.propertyManager?.billerAddressId) {
        hasBillerAddress = true;
        promises.push(fetchBillerAddress(lease.property.propertyManager.billerAddressId));
      } else {
        console.log("No biller address found");
        setBillerAddressLoading(false);
      }

      // Check payment methods from financialTerms
      if (lease.financialTerms?.paymentMethods && lease.financialTerms.paymentMethods.length > 0) {
        // Find the primary payment method or use the first one
        const paymentMethod = lease.financialTerms.paymentMethods.find(method => method.isPrimary) ||
          lease.financialTerms.paymentMethods[0];

        console.log("Selected payment method:", paymentMethod);

        if (paymentMethod) {
          hasPaymentMethod = true;

          if (paymentMethod.type === "Bank Transfer") {
            // Check if bank details are already embedded in the response
            if (paymentMethod.bankDetails) {
              console.log("Using embedded bank details:", paymentMethod.bankDetails);
              setBankDetails(paymentMethod.bankDetails);
              setPaymentMethod("bank");
              setBankDetailsLoading(false);
              setPaymentSettingsLoading(false);
            } else if (paymentMethod.bankDetailsId) {
              // Fallback: fetch bank details if not embedded
              console.log("Fetching bank details for:", paymentMethod.bankDetailsId);
              promises.push(fetchBankDetails(paymentMethod.bankDetailsId));
              setPaymentMethod("bank");
            } else {
              console.log("Bank Transfer selected but no bank details available");
              setBankDetailsLoading(false);
              setPaymentSettingsLoading(false);
            }
          } else if (paymentMethod.type === "Mobile Money") {
            // Check if mobile money details are embedded
            if (paymentMethod.mobileMoneyDetails) {
              console.log("Using embedded mobile money details:", paymentMethod.mobileMoneyDetails);
              setPaymentSettings(paymentMethod.mobileMoneyDetails);
              setPaymentMethod("mobile");
              setBankDetailsLoading(false);
              setPaymentSettingsLoading(false);
            } else if (paymentMethod.details?.businessNumber) {
              // Use the business number directly without API call
              console.log("Using business number from lease:", paymentMethod.details.businessNumber);
              setPaymentSettings({
                shortCode: paymentMethod.details.businessNumber,
                // Add other fields you might need
              });
              setPaymentMethod("mobile");
              setBankDetailsLoading(false);
              setPaymentSettingsLoading(false);
            } else if (paymentMethod.businessNumber) {
              // Business number might be directly on the payment method
              console.log("Using business number directly:", paymentMethod.businessNumber);
              setPaymentSettings({
                shortCode: paymentMethod.businessNumber,
                // Add other fields you might need
              });
              setPaymentMethod("mobile");
              setBankDetailsLoading(false);
              setPaymentSettingsLoading(false);
            } else {
              console.log("Mobile Money selected but no business number available");
              setBankDetailsLoading(false);
              setPaymentSettingsLoading(false);
            }
          } else {
            console.log("Unknown payment type:", paymentMethod.type);
            setBankDetailsLoading(false);
            setPaymentSettingsLoading(false);
          }
        }
      } else {
        console.log("No payment methods found in financialTerms");
      }

      // If no biller address was found, ensure its loading state is false
      if (!hasBillerAddress) {
        setBillerAddressLoading(false);
      }

      // If no payment method was found, ensure loading states are false
      if (!hasPaymentMethod) {
        console.log("No payment method found, setting loading states to false");
        setBankDetailsLoading(false);
        setPaymentSettingsLoading(false);
      }

      // Wait for all promises to settle (if any)
      if (promises.length > 0) {
        console.log("Waiting for", promises.length, "promises to settle");
        const results = await Promise.allSettled(promises);
        console.log("Promise results:", results);

        // Check if any promises were rejected
        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            console.error(`Promise ${index} was rejected:`, result.reason);
          }
        });
      } else {
        console.log("No promises to wait for - all data was embedded or no payment method configured");
      }

    } catch (error) {
      console.error("Error processing payment configuration:", error);
      toastify("Error processing payment configuration: " + error.message, "error");
      setBillerAddressLoading(false);
      setBankDetailsLoading(false);
      setPaymentSettingsLoading(false);
    }
  };

  // Keep the fetch functions as backup (they may still be needed in some cases)
  const fetchBankDetails = async (bankAccountId) => {
    try {
      console.log("Fetching bank details from URL:", `${getBankDetailsForLevyURL}/${facilityId}/${bankAccountId}`);

      const response = await makeRequest2(
        `${getBankDetailsForLevyURL}/${facilityId}/${bankAccountId}`,
        "GET",
      );

      console.log("Bank details response:", response);

      if (response.success) {
        const bankData = extractResponseData(response);
        console.log("Extracted bank data:", bankData);
        setBankDetails(bankData);
        setPaymentMethod("bank");
      } else {
        console.error("Failed to fetch bank details:", response);
        toastify("Failed to fetch bank details: " + (response.message || "Unknown error"), "error");
      }
    } catch (error) {
      console.error("Error fetching bank details:", error);
      toastify("Error fetching bank details: " + error.message, "error");
    } finally {
      console.log("Setting bankDetailsLoading to false");
      setBankDetailsLoading(false);
    }
  };

  const fetchMobilePaymentDetails = async (businessNumber) => {
    try {
      console.log("Fetching mobile payment details for business number:", businessNumber);
      console.log("Using URL:", `${getFacilityPaymentDetailsURL}/${facilityId}/${businessNumber}`);

      const response = await makeRequest2(
        `${getFacilityPaymentDetailsURL}/${facilityId}/${businessNumber}`,
        "GET",
      );

      console.log("Mobile payment response:", response);

      if (response.success) {
        const paymentData = extractResponseData(response);
        console.log("Extracted payment data:", paymentData);
        setPaymentSettings(paymentData);
        setPaymentMethod("mobile");
      } else {
        console.error("Failed to fetch mobile payment details:", response);
        // toastify("Failed to fetch mobile payment details: " + (response.message || "Unknown error"), "error");
      }
    } catch (error) {
      console.error("Error fetching mobile payment details:", error);
      toastify("Error fetching mobile payment details: " + error.message, "error");
    } finally {
      console.log("Setting paymentSettingsLoading to false");
      setPaymentSettingsLoading(false);
      // Also set bankDetailsLoading to false for mobile payments
      setBankDetailsLoading(false);
    }
  };

  const fetchBillerAddress = async (billerAddressId) => {
    try {
      console.log("Fetching biller address from URL:", `${getBillerAddressForLevyURL}/${facilityId}/${billerAddressId}`);

      const response = await makeRequest2(
        `${getBillerAddressForLevyURL}/${facilityId}/${billerAddressId}`,
        "GET",
      );

      console.log("Biller address response:", response);

      if (response.success) {
        const billerData = extractResponseData(response);
        console.log("Extracted biller data:", billerData);
        setBillerAddress(billerData);
      } else {
        console.error("Failed to fetch biller address:", response);
        toastify("Failed to fetch biller address: " + (response.message || "Unknown error"), "error");
      }
    } catch (error) {
      console.error("Error fetching biller address:", error);
      toastify("Error fetching biller address: " + error.message, "error");
    } finally {
      console.log("Setting billerAddressLoading to false");
      setBillerAddressLoading(false);
    }
  };

  // Mark invoice as read
  const markInvoiceAsRead = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `${getInvoice}/${facilityId}/${invoiceId}/markAsRead`,
        "POST",
      );

      if (response.success) {
        setInvoice((prev) => ({
          ...prev,
          viewStatus: {
            ...prev.viewStatus,
            isOpened: true,
            openedAt: new Date(),
          },
        }));
        toastify("Invoice marked as read", "success");
      } else {
        toastify(response.message || "Failed to mark invoice as read", "error");
      }
    } catch (error) {
      toastify("Error marking invoice as read", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle PDF download
  const handleDownloadPDF = async () => {
    const element = document.getElementById("printable-invoice");
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const filename = `Invoice_${invoice?.invoiceNumber}_${timestamp}.pdf`;

    try {
      setLoading(true);
      toastify("Generating PDF...", "info");

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/jpeg", 1.0);
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let position = 0;

      pdf.addImage(
        imgData,
        "JPEG",
        0,
        position,
        imgWidth,
        imgHeight,
        "",
        "FAST",
      );

      let heightLeft = imgHeight - pageHeight;
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(
          imgData,
          "JPEG",
          0,
          position,
          imgWidth,
          imgHeight,
          "",
          "FAST",
        );
        heightLeft -= pageHeight;
      }

      pdf.save(filename);
      toastify("PDF downloaded successfully!", "success");
    } catch (error) {
      console.error("PDF generation error:", error);
      toastify("Failed to generate PDF. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle cash payment success
  const handleCashPaymentSuccess = (payment) => {
    fetchInvoiceData();
  };

  // Helper functions
  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString();
  };

  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return "0.00";
    const currencyCode = invoice?.currency?.code || "KES";

    const numericAmount = parseFloat(amount);
    const formattedNumber = numericAmount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return `${currencyCode} ${formattedNumber}`;
  };

  // Check if there's a balance brought forward
  const hasBalanceBroughtForward = () => {
    return (
      invoice?.balanceBroughtForward !== 0 &&
      invoice?.balanceBroughtForward !== null &&
      invoice?.balanceBroughtForward !== undefined
    );
  };

  // Determine if balanceBroughtForward is a credit or debit
  const isBalanceBroughtForwardCredit = () => {
    return (invoice?.balanceBroughtForward || 0) < 0;
  };

  // Calculate total balance (invoice amount + balance brought forward + penalty)
  const calculateTotalBalance = () => {
    if (!invoice) return 0;

    const totalAmount = parseFloat(invoice.subTotal) || 0;
    const penalty = parseFloat(invoice.penalty) || 0;
    const balanceBroughtForward =
      parseFloat(invoice.balanceBroughtForward) || 0;

    return totalAmount + balanceBroughtForward + penalty;
  };

  // Calculate total due (total balance - amount paid)
  const calculateTotalDue = () => {
    if (!invoice) return 0;

    const totalBalance = calculateTotalBalance();
    const amountPaid = parseFloat(invoice.amountPaid) || 0;

    return totalBalance - amountPaid;
  };

  // Check if final amount is credit or debit
  const isFinalCredit = calculateTotalDue() < 0;

  // Format the balance brought forward
  const formatBalanceBroughtForward = () => {
    if (
      !invoice ||
      !invoice.balanceBroughtForward ||
      invoice.balanceBroughtForward === 0
    ) {
      return formatAmount(0);
    }

    return formatAmount(Math.abs(invoice.balanceBroughtForward));
  };

  // View receipt function for the receipts dialog
  const viewReceipt = (paymentData) => {
    const allowedTypes = [
      "cash",
      "mpesa-transfer",
      "bank-transfer",
      "cheque",
      "payment",
    ];
    const paymentType = (paymentData.type || "").toLowerCase();

    const isAllowedType = allowedTypes.some(
      (type) =>
        paymentType === type || paymentType.includes(type.replace("-", "")),
    );

    if (!isAllowedType) {
      toastify(
        "Receipt viewing is only available for cash, M-Pesa, bank transfers, and cheque payments",
        "info",
      );
      return;
    }

    navigate(`/facility/levy_management/receipt/${paymentData._id}`, {
      state: {
        receipt: paymentData,
        invoice: invoice,
        companyDetails: companyDetails,
        billerAddress: billerAddress,
      },
    });
  };

  // Action template for the data table in the receipts dialog
  const actionBodyTemplate = (rowData) => {
    const allowedTypes = [
      "cash",
      "mpesa-transfer",
      "bank-transfer",
      "cheque",
      "payment",
    ];

    const paymentType = (rowData.type || "").toLowerCase();

    const isAllowedType = allowedTypes.some(
      (type) =>
        paymentType === type || paymentType.includes(type.replace("-", "")),
    );

    return (
      <Button
        icon="fas fa-eye"
        className="p-button-sm p-button-text"
        onClick={() => viewReceipt(rowData)}
        disabled={!isAllowedType}
        tooltip={
          isAllowedType
            ? "View Receipt"
            : "Receipt not available for this payment type"
        }
        tooltipOptions={{ position: "left" }}
      />
    );
  };

  // Define read/unread status styles
  const readStatusStyles = {
    button: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "0.5rem 1rem",
      borderRadius: "20px",
      fontSize: "0.875rem",
      fontWeight: "600",
      border: "none",
      cursor: "pointer",
      transition: "all 0.2s ease",
      marginLeft: "0.75rem",
    },
    unread: {
      backgroundColor: "#ffc107",
      color: "#212529",
    },
    read: {
      backgroundColor: "#28a745",
      color: "#ffffff",
    },
  };

  // Render loading state
  if (loading && !invoice) {
    return (
      <Layout>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "70vh" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span className="ms-2">Loading invoice details...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={styles.pageContainer}>
        {/* Action Bar with Dropdown and Read/Unread Status */}
        <div className={styles.actionCard}>
          <div className={styles.actionCardLeft}>
            <Link
              to="#"
              onClick={() => navigate(-1)}
              className={styles.backLink}
            >
              <i className="ti ti-arrow-narrow-left"></i>
              <span>Back</span>
            </Link>

            {/* Read/Unread Status Button */}
            <div style={{ display: "inline-block", marginLeft: "20px" }}>
              {invoice?.viewStatus?.isOpened === true ? (
                <button
                  style={{
                    ...readStatusStyles.button,
                    ...readStatusStyles.read,
                  }}
                >
                  <i className="fas fa-check-circle me-1"></i>
                  Read
                </button>
              ) : (
                <button
                  style={{
                    ...readStatusStyles.button,
                    ...readStatusStyles.unread,
                  }}
                  onClick={markInvoiceAsRead}
                >
                  <i className="fas fa-exclamation-circle me-1"></i>
                  Unread
                </button>
              )}
            </div>

            {/* Data Loading Indicator */}
            {leaseLoading && (
              <div style={{ display: "inline-block", marginLeft: "20px" }}>
                <span className="text-muted">
                  <i className="fas fa-spinner fa-spin me-1"></i>
                  Loading lease details...
                </span>
              </div>
            )}
          </div>
          <div className={styles.actionCardRight}>
            <InvoiceActionsDropdown
              invoice={invoice}
              facilityId={facilityId}
              handleDownloadPDF={handleDownloadPDF}
              setShowReceiptsDialog={setShowReceiptsDialog}
              onCashPaymentSuccess={handleCashPaymentSuccess}
              loading={loading}
            />
          </div>
        </div>

        {/* Data Status Messages */}
        {allPaymentDataLoaded &&
          invoice?.whatFor?.invoiceType === "Lease" && (
            <div className="mb-3">
              {!bankDetails && !paymentSettings && (
                <div className="alert alert-warning" role="alert">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  No payment method configured for this invoice.
                </div>
              )}
            </div>
          )}

        {/* Show loading indicators */}
        {!allPaymentDataLoaded &&
          invoice?.whatFor?.invoiceType === "Lease" && (
            <div className="mb-3">
              {leaseLoading && (
                <div className="alert alert-info" role="alert">
                  <i className="fas fa-spinner fa-spin me-2"></i>
                  Loading lease details...
                </div>
              )}
              {billerAddressLoading && (
                <div className="alert alert-info" role="alert">
                  <i className="fas fa-spinner fa-spin me-2"></i>
                  Loading biller address...
                </div>
              )}
              {(bankDetailsLoading || paymentSettingsLoading) && (
                <div className="alert alert-info" role="alert">
                  <i className="fas fa-spinner fa-spin me-2"></i>
                  Loading payment details...
                </div>
              )}
            </div>
          )}

        {/* Receipts Dialog */}
        <Dialog
          header="Payment Receipts"
          visible={showReceiptsDialog}
          style={{ width: "50vw" }}
          onHide={() => setShowReceiptsDialog(false)}
          resizable={false}
        >
          <DataTable
            value={invoice?.reconciliationHistory || []}
            responsiveLayout="scroll"
            emptyMessage="No payment records found"
            className="p-datatable-sm"
            paginator
            rows={5}
            rowsPerPageOptions={[5, 10, 20]}
          >
            <Column
              field="date"
              header="Date"
              body={(rowData) => formatDate(rowData.date)}
              sortable
            />
            <Column
              field="amount"
              header="Amount"
              body={(rowData) => formatAmount(rowData.amount)}
              sortable
            />
            <Column
              field="type"
              header="Type"
              sortable
              body={(rowData) => {
                const paymentType = (rowData.type || "").toLowerCase();
                const allowedTypes = [
                  "cash",
                  "mpesa-transfer",
                  "bank-transfer",
                  "cheque",
                  "payment",
                ];
                const isAllowedType = allowedTypes.some(
                  (type) =>
                    paymentType === type ||
                    paymentType.includes(type.replace("-", "")),
                );

                let displayType = rowData.type || "Unknown";
                if (paymentType === "cash") displayType = "Cash";
                else if (
                  paymentType === "mpesa-transfer" ||
                  paymentType.includes("mpesa")
                )
                  displayType = "M-Pesa";
                else if (
                  paymentType === "bank-transfer" ||
                  paymentType.includes("bank")
                )
                  displayType = "Bank Transfer";
                else if (paymentType === "cheque" || paymentType === "check")
                  displayType = "Cheque";
                else if (paymentType === "payment") displayType = "Payment";

                return (
                  <div>
                    {displayType}
                    {isAllowedType && (
                      <i
                        className="fas fa-receipt ml-2"
                        style={{
                          color: "#28a745",
                          fontSize: "0.85em",
                          marginLeft: "5px",
                        }}
                        title="Receipt available"
                      />
                    )}
                  </div>
                );
              }}
            />
            <Column field="paymentReference" header="Reference" sortable />
            <Column
              field="paymentCompletion"
              header="Status"
              sortable
              body={(rowData) => (
                <span
                  className={`badge ${rowData.paymentCompletion === "Completed"
                    ? "bg-success"
                    : rowData.paymentCompletion === "Partial"
                      ? "bg-warning"
                      : rowData.paymentCompletion === "Overpaid"
                        ? "bg-info"
                        : "bg-secondary"
                    }`}
                >
                  {rowData.paymentCompletion}
                </span>
              )}
            />
            <Column
              body={actionBodyTemplate}
              exportable={false}
              style={{ width: "4rem" }}
            />
          </DataTable>
        </Dialog>

        {/* Invoice Content */}
        <div className={styles.invoiceContainer}>
          <div id="printable-invoice" className={styles.invoiceContent}>
            {/* Invoice Status Banner */}
            <div
              className={styles.statusBanner}
              data-status={invoice?.status?.toLowerCase()}
            >
              <span>{invoice?.status}</span>
            </div>

            <div className={styles.invoiceHeader}>
              <div className={styles.invoiceHeaderContent}>
                <div className={styles.headerLeft}>
                  <h1
                    className={styles.invoiceTitleInHeader}
                    style={{
                      marginTop: "-20px",
                      marginLeft: "60%",
                      color: "white",
                      fontSize: "28px",
                      fontWeight: "600",
                    }}
                  >
                    INVOICE
                  </h1>
                  {facilityLogo && (
                    <div
                      className={styles.logoContainer}
                      style={{ marginTop: "0", marginLeft: "60%" }}
                    >
                      <img
                        src={facilityLogo}
                        alt="Company Logo"
                        className={styles.companyLogo}
                      />
                    </div>
                  )}
                </div>
                <div className={styles.headerDetails}>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>INVOICE NO.</span>
                    <span className={styles.value}>
                      {invoice?.invoiceNumber}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>ACCOUNT NO.</span>
                    <span className={styles.value}>
                      {invoice?.accountNumber}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>INVOICE DATE</span>
                    <span className={styles.value}>
                      {formatDate(invoice?.issueDate)}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>DUE DATE</span>
                    <span className={styles.value}>
                      {formatDate(invoice?.dueDate)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.invoiceBody}>
              <div className={styles.addressSection}>
                <div className={styles.addressLayout}>
                  <div className={styles.fromSection}>
                    <h2 className={styles.sectionTitle}>FROM</h2>
                    <div className={styles.addressContent}>
                      {/* Use biller address if available, otherwise fall back to company details */}
                      {billerAddress ? (
                        <>
                          {billerAddress.companyName && (
                            <div>{billerAddress.companyName}</div>
                          )}
                          {billerAddress.address && (
                            <div>{billerAddress.address}</div>
                          )}
                          {billerAddress.postalCode && (
                            <div>{billerAddress.postalCode}</div>
                          )}
                          {(billerAddress.city || billerAddress.country) && (
                            <div>
                              {billerAddress.city}
                              {billerAddress.city && billerAddress.country
                                ? ", "
                                : ""}
                              {billerAddress.country}
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div>{companyDetails?.name || "Company Name"}</div>
                          <div>{companyDetails?.address || "Company Address"}</div>
                          <div>{companyDetails?.country || "Country"}</div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className={styles.billToSection}>
                    <h2 className={styles.sectionTitle}>BILL TO</h2>
                    <div className={styles.addressContent}>
                      <div>{`${invoice?.client?.firstName || ""} ${invoice?.client?.lastName || ""}`}</div>
                      <div>Unit: {invoice?.unit?.name || "N/A"}</div>
                    </div>
                  </div>
                  <div className={styles.totalSection}>
                    <h2 className={styles.sectionTitle}>TOTAL DUE</h2>
                    <div
                      className={`${styles.totalAmount} ${isFinalCredit ? styles.creditAmount : ""}`}
                    >
                      {isFinalCredit
                        ? formatAmount(-Math.abs(calculateTotalDue()))
                        : formatAmount(calculateTotalDue())}
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.itemsGrid}>
                <div className={styles.gridHeader}>
                  <span className={styles.columnHeader}>DESCRIPTION</span>
                  <span className={`${styles.columnHeader} ${styles.right}`}>
                    UNIT PRICE
                  </span>
                  <span className={`${styles.columnHeader} ${styles.right}`}>
                    QTY
                  </span>
                  <span className={`${styles.columnHeader} ${styles.right}`}>
                    AMOUNT
                  </span>
                </div>

                {invoice?.items?.map((item, index) => (
                  <div key={index} className={styles.gridRow}>
                    <span className={styles.value}>{item.description}</span>
                    <span className={`${styles.value} ${styles.right}`}>
                      {formatAmount(item.unitPrice)}
                    </span>
                    <span className={`${styles.value} ${styles.right}`}>
                      {item.quantity}
                    </span>
                    <span className={`${styles.value} ${styles.right}`}>
                      {formatAmount(item.quantity * item.unitPrice)}
                    </span>
                  </div>
                ))}

                <div className={styles.calculations}>
                  <div className={styles.calculationsGrid}>
                    {/* Invoice Calculation Section */}
                    <div className={styles.calculationsRow}>
                      <span className={styles.calcLabel}>SUBTOTAL</span>
                      <span className={styles.calcValue}>
                        {formatAmount(invoice?.subTotal)}
                      </span>
                    </div>
                    <div className={styles.calculationsRow}>
                      <span className={styles.calcLabel}>
                        TAX (
                        {((invoice?.tax / invoice?.subTotal) * 100).toFixed(
                          0,
                        ) || 0}
                        %)
                      </span>
                      <span className={styles.calcValue}>
                        {formatAmount(invoice?.tax)}
                      </span>
                    </div>
                    <div className={styles.calculationsRow}>
                      <span className={styles.calcLabel}>INVOICE TOTAL</span>
                      <span className={styles.calcValue}>
                        {formatAmount(invoice?.subTotal + invoice?.tax)}
                      </span>
                    </div>

                    {/* Divider */}
                    <div className={styles.divider}></div>

                    {/* Balance Brought Forward - Show if exists */}
                    {hasBalanceBroughtForward() && (
                      <div className={styles.calculationsRow}>
                        <span className={styles.calcLabel}>
                          BALANCE B/FORWARD
                        </span>
                        <span
                          className={`${styles.calcValue} ${isBalanceBroughtForwardCredit() ? styles.creditAmount : styles.debitAmount}`}
                        >
                          {isBalanceBroughtForwardCredit()
                            ? "(" + formatBalanceBroughtForward() + ")"
                            : formatBalanceBroughtForward()}
                        </span>
                      </div>
                    )}

                    {/* Total Balance (Invoice + Balance Brought Forward) */}
                    <div className={styles.calculationsRow}>
                      <span className={styles.calcLabel}>AMOUNT PAYABLE</span>
                      <span className={styles.calcValue}>
                        {formatAmount(calculateTotalBalance())}
                      </span>
                    </div>

                    {/* Penalty Amount */}
                    {(invoice?.penalty && parseFloat(invoice.penalty) > 0) ||
                      (invoice?.invoiceNote &&
                        invoice.invoiceNote.includes("Penalty applied")) ? (
                      <>
                        <div className={styles.calculationsRow}>
                          <span className={styles.calcLabel}>PENALTY</span>
                          <span
                            className={`${styles.calcValue}`}
                            style={{ color: "#dc3545", fontWeight: "600" }}
                          >
                            {invoice.penalty
                              ? formatAmount(invoice.penalty)
                              : "Applied"}
                          </span>
                        </div>
                        {invoice?.invoiceNote &&
                          invoice.invoiceNote.includes("Penalty applied") && (
                            <div
                              className={styles.calculationsRow}
                              style={{
                                fontSize: "0.85em",
                                fontStyle: "italic",
                              }}
                            >
                              <span className={styles.calcLabel}></span>
                              <span
                                className={styles.calcValue}
                                style={{ color: "#6c757d", fontSize: "0.85em" }}
                              >
                                {invoice.invoiceNote.replace(
                                  "Penalty applied: ",
                                  "",
                                )}
                              </span>
                            </div>
                          )}
                      </>
                    ) : null}

                    {/* Divider */}
                    <div className={styles.divider}></div>

                    {/* Amount Paid */}
                    <div className={styles.calculationsRow}>
                      <span className={styles.calcLabel}>PAYMENT</span>
                      <span className={styles.calcValue}>
                        {formatAmount(invoice?.amountPaid)}
                      </span>
                    </div>

                    {/* Total Due */}
                    <div
                      className={`${styles.calculationsRow} ${styles.balance}`}
                    >
                      <span className={styles.calcLabel}>TOTAL DUE</span>
                      <span
                        className={`${styles.calcValue} ${isFinalCredit ? styles.creditAmount : styles.debitAmount}`}
                      >
                        {isFinalCredit
                          ? formatAmount(-Math.abs(calculateTotalDue()))
                          : formatAmount(calculateTotalDue())}
                      </span>
                    </div>
                  </div>

                  <div className={styles.totalRow}>
                    <span className={styles.totalLabel}>TOTAL DUE</span>
                    <span
                      className={`${styles.totalValue} ${isFinalCredit ? styles.creditAmount : styles.debitAmount}`}
                    >
                      {isFinalCredit
                        ? "(" +
                        formatAmount(Math.abs(calculateTotalDue())) +
                        ")"
                        : formatAmount(calculateTotalDue())}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Details Footer */}
              {allPaymentDataLoaded ? (
                // Show payment details only when all data is loaded
                paymentMethod === "mobile" && paymentSettings ? (
                  <div
                    className={styles.footer}
                    style={{
                      backgroundColor: "#f0f0f0",
                      padding: "12px 20px",
                      marginLeft: "-20px",
                      marginRight: "-20px",
                      marginBottom: "-20px",
                      borderBottomLeftRadius: "8px",
                      borderBottomRightRadius: "8px",
                    }}
                  >
                    <div className="row align-items-center">
                      <div className="col-sm-4">
                        <div
                          className={styles.paymentDetails}
                          style={{ marginBottom: 0 }}
                        >
                          <p
                            style={{
                              fontSize: "12px",
                              fontWeight: "bold",
                              marginBottom: "4px",
                            }}
                          >
                            Payment Details:
                          </p>
                          <p style={{ fontSize: "11px", marginBottom: "2px" }}>
                            <strong>Bank:</strong> Mpesa
                          </p>
                          <p style={{ fontSize: "11px", marginBottom: 0 }}>
                            <strong>Paybill:</strong>{" "}
                            {paymentSettings?.shortCode || "N/A"}
                          </p>
                        </div>
                      </div>

                      <div className="col-sm-5 text-center">
                        <img
                          src="/signature.png"
                          alt="Digital Signature"
                          style={{
                            width: "120px",
                            height: "45px",
                            objectFit: "contain",
                          }}
                        />
                        <p
                          style={{
                            textAlign: "center",
                            marginTop: "2px",
                            marginBottom: "2px",
                            fontSize: "10px",
                          }}
                        >
                          ___________________________
                        </p>
                        <p
                          style={{
                            textAlign: "center",
                            fontSize: "9px",
                            marginBottom: 0,
                          }}
                        >
                          Authorized Signature
                        </p>
                      </div>

                      {/* eTims QR Code */}
                      {invoice?.ticketUrl && (
                        <div className="col-sm-3 text-center">
                          <EtimsQRCode
                            ticketUrl={invoice.ticketUrl}
                            size={80}
                          />
                        </div>
                      )}
                    </div>

                    <div className="row">
                      <div className="col-12 pt-1">
                        <p
                          style={{
                            textAlign: "center",
                            fontSize: "11px",
                            marginBottom: 0,
                          }}
                        >
                          To complete your payment, use paybill
                          <strong>
                            {" "}
                            {paymentSettings?.shortCode || "N/A"}{" "}
                          </strong>
                          with account number
                          <strong> {invoice?.accountNumber || "N/A"}</strong>.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : paymentMethod === "bank" && bankDetails ? (
                  <div
                    className={styles.footer}
                    style={{
                      backgroundColor: "#f0f0f0",
                      padding: "12px 20px",
                      marginLeft: "-20px",
                      marginRight: "-20px",
                      marginBottom: "-20px",
                      borderBottomLeftRadius: "8px",
                      borderBottomRightRadius: "8px",
                    }}
                  >
                    <div className="row">
                      <div className="col-12 text-center pb-2">
                        <h6
                          className={styles.detailsTitle}
                          style={{
                            margin: "0 auto",
                            fontWeight: "bold",
                            fontSize: "12px",
                          }}
                        >
                          PAYMENT DETAILS
                        </h6>
                      </div>
                    </div>

                    <div className="row align-items-center">
                      <div className="col-sm-3">
                        <div
                          className={styles.paymentDetails}
                          style={{ marginBottom: 0 }}
                        >
                          <p style={{ fontSize: "11px", marginBottom: "2px" }}>
                            <strong>Bank:</strong>{" "}
                            {bankDetails?.bankName || "N/A"}
                          </p>
                          <p style={{ fontSize: "11px", marginBottom: "2px" }}>
                            <strong>Account:</strong>{" "}
                            {bankDetails?.accountName || "N/A"}
                          </p>
                          <p style={{ fontSize: "11px", marginBottom: 0 }}>
                            <strong>Acc No:</strong>{" "}
                            {bankDetails?.accountNumber || "N/A"}
                          </p>
                        </div>
                      </div>

                      <div className="col-sm-3">
                        <div
                          className={styles.additionalDetails}
                          style={{ marginBottom: 0 }}
                        >
                          <p style={{ fontSize: "11px", marginBottom: "2px" }}>
                            <strong>Branch:</strong>{" "}
                            {bankDetails?.branchName || "N/A"}
                          </p>
                          <p style={{ fontSize: "11px", marginBottom: "2px" }}>
                            <strong>Code:</strong>{" "}
                            {bankDetails?.branchCode || "N/A"}
                          </p>
                          <p style={{ fontSize: "11px", marginBottom: 0 }}>
                            <strong>Swift:</strong>{" "}
                            {bankDetails?.bankCode || "N/A"}
                          </p>
                        </div>
                      </div>

                      <div className="col-sm-4 text-center">
                        <img
                          src="/signature.png"
                          alt="Digital Signature"
                          style={{
                            width: "120px",
                            height: "45px",
                            objectFit: "contain",
                          }}
                        />
                        <p
                          style={{
                            marginTop: "0px",
                            marginBottom: "2px",
                            fontSize: "10px",
                          }}
                        >
                          ___________________________
                        </p>
                        <p style={{ fontSize: "9px", marginBottom: 0 }}>
                          Authorized Signature
                        </p>
                      </div>

                      {/* eTims QR Code */}
                      {invoice?.ticketUrl && (
                        <div className="col-sm-2 text-center">
                          <EtimsQRCode
                            ticketUrl={invoice.ticketUrl}
                            size={80}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  // Default footer when no payment method is configured
                  <div
                    className={styles.footer}
                    style={{
                      backgroundColor: "#f0f0f0",
                      padding: "12px 20px",
                      marginLeft: "-20px",
                      marginRight: "-20px",
                      marginBottom: "-20px",
                      borderBottomLeftRadius: "8px",
                      borderBottomRightRadius: "8px",
                    }}
                  >
                    <div className="row align-items-center justify-content-center">
                      {invoice?.whatFor?.invoiceType === "Lease" ? (
                        <div className="col-12 text-center">
                          <div className={styles.noPaymentMethod}>
                            <i
                              className="fas fa-exclamation-triangle text-warning"
                              style={{
                                fontSize: "1.5rem",
                                marginBottom: "0.5rem",
                              }}
                            ></i>
                            <p
                              className="text-muted mb-0"
                              style={{ fontSize: "12px" }}
                            >
                              Payment Method Not Configured
                            </p>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="col-auto text-center">
                            <p
                              style={{
                                marginTop: "5px",
                                marginBottom: "2px",
                                fontSize: "10px",
                              }}
                            >
                              ___________________________
                            </p>
                            <p
                              className="text-muted"
                              style={{ fontSize: "9px", marginBottom: 0 }}
                            >
                              Authorized Signature
                            </p>
                          </div>
                          {/* eTims QR Code */}
                          {invoice?.ticketUrl && (
                            <div className="col-auto ms-4">
                              <EtimsQRCode
                                ticketUrl={invoice.ticketUrl}
                                size={80}
                              />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )
              ) : (
                // Loading state for payment details
                invoice?.whatFor?.invoiceType === "Lease" && (
                  <div
                    className={styles.footer}
                    style={{
                      backgroundColor: "#f8f9fa",
                      borderTop: "2px solid #dee2e6",
                    }}
                  >
                    <div className="row px-5 py-4">
                      <div className="col-12 text-center">
                        <div className={styles.loadingPaymentDetails}>
                          <div
                            className="spinner-border text-primary mb-3"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <h5 className="text-muted">
                            Loading Payment Details
                          </h5>
                          <p className="text-muted mb-0">
                            Please wait while we fetch the payment
                            information...
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default InvoicePage;